import { useState, useEffect, useCallback, useContext } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import CourseService from "../../../services/CourseService";
import AlertService from "../../../services/AlertService";
import AuthContext from "../../../auth/AuthContext";
import { Skeleton } from "primereact/skeleton";
import iconCourses from "../../../assets/icons/Users.svg";
import iconDocuments from "../../../assets/icons/DocumentIcon.svg";
import iconBayReport from "../../../assets/icons/CyberRangeReportIcon.svg";
import iconCatalogue from "../../../assets/icons/CatalogueIcon.svg";
import iconReleaseIncidents from "../../../assets/icons/ReleaseIncidents.svg";
import iconCyberRange from "../../../assets/icons/CyberRangeIcon.svg";
import iconAssessment from "../../../assets/icons/AssessmentIcon.svg";
import EditIcon from '../../../assets/icons/edit.svg';
import { ThemeContext } from "../../../theme/ThemeContext";
import WhiteEditIcon from '../../../assets/icons/WhiteEditIcon.svg';
// import Moment from "moment";

const getCards = (courseName, labType) => {
  const vmPathMap = {
    AZURE: `/courses/${courseName}/vms`,
    LOCAL: `/courses/${courseName}/lvms`,
    GUACAMOLE: `/courses/${courseName}/gvms`,
  };

  return [
    { title: "Catalogue", icon: iconCatalogue, editPath: `/courses/${courseName}/catalogues`, viewPath: `/courses/${courseName}/catalogue`, hasView: true },
    { title: "Bay", icon: iconCyberRange, viewPath: `/bay`, hasView: true },
    { title: "Users", icon: iconCourses, editPath: `/courses/${courseName}/users`, hasView: false },
    {
      title: "VM/User-Mapping",
      icon: iconAssessment,
      editPath: vmPathMap[labType] || "",
      hasView: false,
    },
    { title: "Documents", icon: iconDocuments, editPath: `/courses/${courseName}/documents`, viewPath: `/courses/${courseName}/view-documents`, hasView: true },
    { title: "Bay Sessions", icon: iconBayReport, editPath: `/courses/${courseName}/bay-sessions`, hasView: false   },
    { title: "Release Incidents", icon: iconReleaseIncidents, editPath: `/courses/${courseName}/release-incidents`, hasView: false },
  ];
};

const CourseDetails = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";

  const navigate = useNavigate();
  const { state } = useLocation();
  const { courseName } = useParams();
  const authContext = useContext(AuthContext);

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const courseAccess = authContext.getPermissions("courses");

  if (courseAccess.filter((f) => f.access.includes(1)).length === 0) {
    navigate("/");
  }
  const showAdd = courseAccess.filter((f) => f.access.includes(2)).length > 0;
  const showEdit = courseAccess.filter((f) => f.access.includes(3)).length > 0;
  const showRead = courseAccess.filter((f) => f.access.includes(7)).length > 0;

 const loadCourse = useCallback(async (id, signal) => { 
    setLoading(true);
    try {
      const data = await CourseService.Get(id, authContext, signal); 

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        setCourse(data.data);
      } else {
        AlertService.error(data.errorMessage);
        setCourse(null);
      }
    } catch (error) {
      if (error.name === "AbortError") return;
      AlertService.error("Failed to load course");
      setCourse(null);
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [authContext]);

useEffect(() => {
    const controller = new AbortController(); 

    const courseId = state?.id || sessionStorage.getItem("selectedCourseId");
    if (courseId) {
      loadCourse(courseId, controller.signal); 
    } else {
      navigate("/courses");
    }

    return () => controller.abort();
  }, [state, loadCourse, navigate]);


const handleCardClick = (path) => {
  const courseId = state?.id || sessionStorage.getItem("selectedCourseId");
  if (!loading && path) {
    navigate(path, { state: { id: courseId, courseName } });
  }
};

const handleEditClick = (path) => {
  const courseId = state?.id || sessionStorage.getItem("selectedCourseId");
  if (!loading && path) {
    navigate(path, { state: { id: courseId, courseName } });
  }
};


  const cards = getCards(courseName, course?.labType);

  const visibleCards = cards.filter(card => {
    if (!showAdd && !showRead) {
      return false;
    }
    if (showAdd) {
      return true;
    }
    if (showRead && !showAdd) {
      return ["Catalogue", "Bay", "Documents"].includes(card.title);
    }
    return false;
  });

  const CourseCards = ({ icon, title, onClick, onEditClick, loading, hasView }) => (
    <div
      className="d-flex align-items-center gap-3"
      style={{ cursor: hasView ? "pointer" : "default" }}
      onClick={loading || !hasView ? undefined : onClick}
    >
      {loading ? (
        <Skeleton shape="circle" size="54px" />
      ) : (
        <img src={icon} alt={title} style={{ width: "54px", height: "54px" }} />
      )}

      {loading ? (
        <Skeleton width="110px" height="1.5rem" />
      ) : (
        <span style={{ fontSize: "20px" }}>{title}</span>
      )}

      {loading ? (
        <Skeleton shape="circle" size="24px" />
      ) : (
        (showEdit || showAdd) && title !== "Bay" && (
          <img
            src={isDark ? WhiteEditIcon : EditIcon}
            alt="edit"
            style={{ cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation();
              onEditClick();
            }}
          />
        )
      )}
    </div>
  );

  return (
    <div className="container-fluid custom-container pt-3 mt-3">
      <div className="rounded p-4 mb-4 charts" style={{ minHeight: "150px" }}>
        {loading ? (
          <>
            <Skeleton width="30%" height="1.5rem" className="mb-3" />
            <Skeleton width="100%" height="1rem" className="mb-2" />
            <Skeleton width="90%" height="1rem" className="mb-2" />
            <Skeleton width="80%" height="1rem" />
          </>
        ) : course ? (
          <>
            <p className="mb-3 fw-bold">
              Course Name: <span className="fw-normal">{course.courseName}</span>
            </p> 
            {/* <p className="mb-3 fw-bold">
              Course Code: <span className="fw-normal">{course.courseCode}</span>
            </p>
            <p className="mb-3 fw-bold">
              Firm Type: <span className="fw-normal">{course.firmTypeDesc}</span>
            </p>
            <p className="mb-3 fw-bold">
              Planned Start Date: <span className="fw-normal">{Moment(course.plannedStartDate).format("DD/MM/YYYY")}</span>
            </p>
            <p className="mb-3 fw-bold">
              Planned End Date: <span className="fw-normal">{Moment(course.plannedEndDate).format("DD/MM/YYYY")}</span>
            </p> */}
            <p className="mb-1 fontColor2 fw-bold">
              Description:{" "}
              <span className="fw-normal">
                {course.description || "No description available"}
              </span>
            </p>
          </>
        ) : (
          <p>Course not found</p>
        )}
      </div>
      {visibleCards.length > 0 && (
        <div className="d-flex justify-content-md-start align-items-start gap-5 flex-wrap">
          {visibleCards.map((card, idx) => (
            <CourseCards
              key={idx}
              title={card.title}
              icon={card.icon}
              hasView={card.hasView}
              onEditClick={() => handleEditClick(card.editPath)}
              onClick={() => handleCardClick(card.viewPath)}
              loading={loading}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CourseDetails;